import { useState } from 'react';
import { Modal, Form, FormField, Input, Dropdown, TextArea, Button, Message } from 'semantic-ui-react';
import { IcctDatePicker } from '../../common/IcctDatePicker';
import { resolveRoadblock, FieldValidationError } from './roadblockService';
import { formatDateToYyyyMmDd } from '../../../utils/formatDate';
import { TIME_UNIT_OPTIONS } from './constants';

const MAX_NOTE = 500;
const DECIMAL_RE = /^\d*\.?\d*$/;

export const ResolveRoadblockModal = ({ open, roadblockId, onClose, onResolved }) => {
    const [resolutionDate, setResolutionDate] = useState(null);
    const [resolutionNote, setResolutionNote] = useState('');
    const [leaderTimeSpent, setLeaderTimeSpent] = useState('');
    const [leaderTimeUnit, setLeaderTimeUnit] = useState('hours');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [inputErrors, setInputErrors] = useState({});

    const handleClose = () => {
        setResolutionDate(null);
        setResolutionNote('');
        setLeaderTimeSpent('');
        setLeaderTimeUnit('hours');
        setError('');
        setInputErrors({});
        onClose();
    };

    const validate = () => {
        const errors = {};
        if (!resolutionDate) errors.resolutionDate = 'End date is required.';
        if (!resolutionNote.trim()) errors.resolutionNote = 'Resolution note is required.';
        if (!leaderTimeSpent || Number(leaderTimeSpent) <= 0)
            errors.leaderTimeSpent = 'Time spent is required and must be greater than 0.';
        setInputErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        setError('');
        try {
            await resolveRoadblock(roadblockId, {
                resolution_note: resolutionNote.trim(),
                resolution_date: formatDateToYyyyMmDd(resolutionDate),
                leader_time_spent: leaderTimeSpent,
                leader_time_unit: leaderTimeUnit,
            });
            handleClose();
            onResolved();
        } catch (err) {
            if (err instanceof FieldValidationError) {
                setInputErrors(err.fields);
            } else {
                setError(err.message);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose} size="small">
            <Modal.Header>Resolve Roadblock #{roadblockId}</Modal.Header>
            <Modal.Content>
                <p style={{ color: '#666' }}>Fill key information to resolve this roadblock.</p>

                {error && <Message negative>{error}</Message>}

                <Form>
                    <FormField required error={!!inputErrors.leaderTimeSpent}>
                        <label>Time Spent to Resolve Roadblock</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Input
                                placeholder="e.g., 2, 3.25"
                                value={leaderTimeSpent}
                                maxLength={6}
                                style={{ flex: 1 }}
                                onChange={(e, { value }) => {
                                    if (DECIMAL_RE.test(value)) {
                                        setLeaderTimeSpent(value);
                                        if (inputErrors.leaderTimeSpent)
                                            setInputErrors(prev => ({ ...prev, leaderTimeSpent: null }));
                                    }
                                }}
                            />
                            <Dropdown
                                selection
                                options={TIME_UNIT_OPTIONS}
                                value={leaderTimeUnit}
                                onChange={(e, { value }) => setLeaderTimeUnit(value)}
                                style={{ minWidth: '7.5rem' }}
                            />
                        </div>
                        <div style={{ color: '#888', fontSize: '0.8em', marginTop: '0.3em' }}>
                            1 day = 8 hrs &nbsp;&middot;&nbsp; 1 week = 40 hrs &nbsp;&middot;&nbsp; 1 month = 160 hrs
                        </div>
                        {inputErrors.leaderTimeSpent && (
                            <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                {inputErrors.leaderTimeSpent}
                            </div>
                        )}
                    </FormField>

                    <FormField required error={!!inputErrors.resolutionDate}>
                        <label>Roadblock End Date</label>
                        <IcctDatePicker
                            keyName="resolutionDate"
                            value={resolutionDate}
                            maxDate={new Date()}
                            onChange={(key, value) => {
                                setResolutionDate(value);
                                if (inputErrors.resolutionDate) {
                                    setInputErrors(prev => ({ ...prev, resolutionDate: null }));
                                }
                            }}
                            error={!!inputErrors.resolutionDate}
                        />
                        {inputErrors.resolutionDate && (
                            <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                {inputErrors.resolutionDate}
                            </div>
                        )}
                    </FormField>

                    <FormField required error={!!inputErrors.resolutionNote}>
                        <label>Resolution Notes</label>
                        <TextArea
                            placeholder="Describe how the roadblock was resolved..."
                            maxLength={MAX_NOTE}
                            value={resolutionNote}
                            onChange={(e, { value }) => {
                                setResolutionNote(value);
                                if (inputErrors.resolutionNote) {
                                    setInputErrors(prev => ({ ...prev, resolutionNote: null }));
                                }
                            }}
                            rows={4}
                        />
                        <div style={{ color: '#666', fontSize: '0.85em', marginTop: '0.25em', textAlign: 'right' }}>
                            {resolutionNote.length}/{MAX_NOTE} characters
                        </div>
                        {inputErrors.resolutionNote && (
                            <div style={{ color: '#9f3a38', fontSize: '0.85em' }}>
                                {inputErrors.resolutionNote}
                            </div>
                        )}
                    </FormField>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    primary
                    onClick={handleSubmit}
                    loading={saving}
                    disabled={saving}
                >
                    Mark as Resolved
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
